import { useState, useEffect, useMemo } from "react";
import { useAuth, ROLES_FULL_ACCESS } from "../App";
import axios from "axios";
import { API } from "../App";
import UserFicheSheet from "../components/users/UserFicheSheet";
import OrgChart from "../components/users/OrgChart";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2, Search, Users, GitBranch, Grid3X3 } from "lucide-react";
import { toast } from "sonner";

const ROLE_CONFIG = {
  admin:         { label: "Admin",         className: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" },
  hr:            { label: "RH",            className: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" },
  manager:       { label: "Manager",       className: "bg-[#E8F5E9] text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]" },
  collaborateur: { label: "Collaborateur", className: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300" },
  employee:      { label: "Collaborateur", className: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300" },
  comptabilité:  { label: "Comptabilité",  className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
};

const DEPT_OPTIONS = [
  "Expert métier",
  "Ressources Humaines",
  "Direction",
  "Technique",
];

const getInitials = (first, last) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();

const TrombPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [desktopView, setDesktopView] = useState("hierarchy"); // "hierarchy" | "grid"

  const canEdit = ROLES_FULL_ACCESS.includes(user?.role);

  useEffect(() => {
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const isFullAccess = ROLES_FULL_ACCESS.includes(user?.role);
      const endpoint = isFullAccess ? `${API}/users` : `${API}/users/directory`;
      const res = await axios.get(endpoint);
      setUsers(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des collaborateurs");
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
      const matchSearch =
        !search ||
        fullName.includes(search.toLowerCase()) ||
        (u.position || "").toLowerCase().includes(search.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(search.toLowerCase());
      const matchDept = filterDept === "all" || u.department === filterDept;
      const matchRole = filterRole === "all" || u.role === filterRole;
      return matchSearch && matchDept && matchRole;
    });
  }, [users, search, filterDept, filterRole]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] dark:text-[#4B8BF4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tromb-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Trombinoscope
          </h1>
          {/* Mobile : total sans filtre | Desktop : total filtré */}
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base sm:hidden">
            {users.length} collaborateur{users.length !== 1 ? "s" : ""}
          </p>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base hidden sm:block">
            {filtered.length} collaborateur{filtered.length !== 1 ? "s" : ""}
            {users.length !== filtered.length && ` sur ${users.length}`}
          </p>
        </div>

        {/* Stats chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(
            users.reduce((acc, u) => {
              const role = u.role === "employee" ? "collaborateur" : u.role;
              acc[role] = (acc[role] || 0) + 1;
              return acc;
            }, {})
          ).map(([role, count]) => {
            const rc = ROLE_CONFIG[role] || ROLE_CONFIG.collaborateur;
            return (
              <Badge key={role} className={`${rc.className} border-0 text-xs`}>
                {count} {rc.label}{count > 1 ? "s" : ""}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* ── MOBILE : organigramme ── */}
      <div className="sm:hidden border dark:border-[#1A3050] rounded-2xl bg-white dark:bg-[#0B1628] p-2">
        <OrgChart
          users={users}
          onOpen={canEdit ? (id) => setSelectedUserId(id) : undefined}
          canClick={canEdit}
        />
      </div>

      {/* ── DESKTOP ── */}
      <div className="hidden sm:block space-y-4">
        {/* Toolbar: toggle + filtres */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Vue toggle */}
          <div className="flex items-center gap-1 border dark:border-[#1A3050] rounded-lg p-1 bg-white dark:bg-[#0B1628] shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDesktopView("hierarchy")}
              className={`gap-1.5 h-8 px-3 text-xs rounded-md ${
                desktopView === "hierarchy"
                  ? "bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                  : "text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-100 dark:hover:bg-[#112240]"
              }`}
            >
              <GitBranch className="w-3.5 h-3.5" />
              Organigramme
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDesktopView("grid")}
              className={`gap-1.5 h-8 px-3 text-xs rounded-md ${
                desktopView === "grid"
                  ? "bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                  : "text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-100 dark:hover:bg-[#112240]"
              }`}
            >
              <Grid3X3 className="w-3.5 h-3.5" />
              Grille
            </Button>
          </div>

          {/* Filtres — uniquement en mode grille */}
          {desktopView === "grid" && (
            <>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  placeholder="Rechercher par nom, poste, email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                />
              </div>
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger className="w-full sm:w-48 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                  <SelectValue placeholder="Département" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les départements</SelectItem>
                  {DEPT_OPTIONS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full sm:w-40 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                  <SelectValue placeholder="Rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="hr">RH</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="collaborateur">Collaborateur</SelectItem>
                  <SelectItem value="comptabilité">Comptabilité</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {/* ── Vue Organigramme hiérarchique ── */}
        {desktopView === "hierarchy" && (
          <div className="border dark:border-[#1A3050] rounded-2xl bg-white dark:bg-[#0B1628] p-6 min-h-[300px]">
            <OrgChart
              users={users}
              mode="hierarchy"
              onOpen={canEdit ? (id) => setSelectedUserId(id) : undefined}
              canClick={canEdit}
            />
          </div>
        )}

        {/* ── Vue Grille ── */}
        {desktopView === "grid" && (
          filtered.length === 0 ? (
            <div className="text-center py-16 text-[#9CA3AF] dark:text-[#3D607E]">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun collaborateur trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filtered.map((u) => {
                const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.collaborateur;
                return (
                  <div
                    key={u.id}
                    onClick={() => canEdit && setSelectedUserId(u.id)}
                    className={`flex flex-col items-center text-center p-4 rounded-2xl border dark:border-[#1A3050] bg-white dark:bg-[#0B1628] shadow-sm transition-all duration-200 ${
                      canEdit
                        ? "cursor-pointer hover:-translate-y-1 hover:shadow-md hover:border-[#1A4D2E]/40 dark:hover:border-[#1A4D2E]/60 hover:bg-[#F0FAF4] dark:hover:bg-[#112240]"
                        : ""
                    }`}
                  >
                    <Avatar className="w-16 h-16 mb-3 ring-2 ring-white dark:ring-[#0B1628] shadow-sm">
                      <AvatarImage src={u.avatar_url} className="object-cover" />
                      <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-[#1A4D2E] to-[#2D7A4E] text-white dark:from-[#1A4D2E] dark:to-[#143D24]">
                        {getInitials(u.first_name, u.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-semibold text-sm text-[#111827] dark:text-[#D6E4F5] leading-tight truncate w-full">
                      {u.first_name} {u.last_name}
                    </p>
                    {u.position && (
                      <p className="text-xs text-[#6B7280] dark:text-[#7EA8CE] mt-0.5 truncate w-full">
                        {u.position}
                      </p>
                    )}
                    {u.department && (
                      <p className="text-[10px] text-[#9CA3AF] dark:text-[#3D607E] mt-0.5 truncate w-full">
                        {u.department}
                      </p>
                    )}
                    <Badge className={`mt-2 text-[10px] px-2 py-0 ${rc.className} border-0`}>
                      {rc.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Fiche sheet — HR/admin only */}
      {canEdit && (
        <UserFicheSheet
          userId={selectedUserId}
          open={!!selectedUserId}
          onClose={() => setSelectedUserId(null)}
          allUsers={users}
          onRefresh={fetchUsers}
          onOpenUser={(id) => setSelectedUserId(id)}
        />
      )}
    </div>
  );
};

export default TrombPage;
